import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  getProjects, getGruposPlanta, getOts, getCategoriasServicio, crearOtServicio,
} from '../../api/client'
import { tituloOt, miembrosDe, responsableTrasGrupo } from './otLogica'
import { hoyLocal } from '../partes/fechas'
import SelectorProyecto from '../SelectorProyecto'

const PRIORIDADES = ['critica', 'alta', 'media', 'baja']
const VACIO = {
  categoria_id: '', descripcion: '', proyecto_id: '', ot_relacionada: '', prioridad: '', fecha_compromiso: '',
  grupo_id: '', responsable_id: '', horas_estimadas: '',
}

// "Nueva solicitud" with the origin "Servicio": internal work with no OF -- a
// quality control, an inspection, an infrastructure improvement, a plant task
// -- so the hours worked on it are counted. Created already planned (function
// crear_ot_servicio): what it is for and its planning at once; no draft, no
// admission; the first work assigned starts it. Numbered OT-<n>S.
export default function FormServicio() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [categorias, setCategorias] = useState([])
  const [proyectos, setProyectos] = useState([])
  const [grupos, setGrupos] = useState([])
  const [ots, setOts] = useState([])
  const [f, setF] = useState(VACIO)
  const [faltan, setFaltan] = useState([])
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    getCategoriasServicio().then(setCategorias).catch(() => {})
    getProjects().then(ps => setProyectos(ps.filter(p => p.is_active !== false))).catch(() => {})
    getGruposPlanta().then(setGrupos).catch(() => {})
    getOts().then(os => setOts(os.filter(o => o.tipo !== 'servicio' && !['borrador', 'cancelada'].includes(o.estado)))).catch(() => {})
  }, [])

  // The related OT, picked by typing its code or item ("OT-12 · Soporte").
  const opcionesOt = useMemo(() => ots.map(o => ({ id: o.id, txt: `${o.codigo}${tituloOt(o) ? ` · ${tituloOt(o)}` : ''}` })), [ots])
  const relacionada = opcionesOt.find(o => o.txt === f.ot_relacionada || o.txt.split(' · ')[0].toLowerCase() === f.ot_relacionada.trim().toLowerCase())

  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setFaltan([]); setError('') }
  const elegirGrupo = (id) => {
    // The responsable is one of the group's people: kept if in it, else its supervisor.
    setF(x => ({ ...x, grupo_id: id, responsable_id: responsableTrasGrupo(grupos, id, x.responsable_id) }))
    setFaltan([])
  }

  const crear = async (e) => {
    e.preventDefault()
    const fs = []
    if (!f.categoria_id) fs.push('categoria')
    if (!f.descripcion.trim()) fs.push('descripcion')
    if (!f.proyecto_id) fs.push('proyecto')
    if (f.ot_relacionada.trim() && !relacionada) fs.push('ot_relacionada')
    if (!f.prioridad) fs.push('prioridad')
    if (!f.fecha_compromiso) fs.push('fecha_compromiso')
    else if (f.fecha_compromiso < hoyLocal()) fs.push('fecha_compromiso_pasada')
    if (!f.grupo_id) fs.push('grupo')
    if (!f.responsable_id) fs.push('responsable')
    if (fs.length) { setFaltan(fs); return }
    setGuardando(true)
    try {
      const ot = await crearOtServicio({
        categoria_id: Number(f.categoria_id), descripcion: f.descripcion.trim(),
        proyecto_id: f.proyecto_id ? Number(f.proyecto_id) : null, ot_relacionada_id: relacionada?.id ?? null,
        prioridad: f.prioridad, fecha_compromiso: f.fecha_compromiso, grupo_id: Number(f.grupo_id),
        responsable_id: Number(f.responsable_id),
        ...(f.horas_estimadas !== '' ? { horas_estimadas: Number(f.horas_estimadas) } : {}),
      })
      navigate(`/operations/planta/ot?ot=${ot.id}`)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'validacion') setFaltan(d.faltan || [])
      else setError(d?.message || t('ot.panel.fallo'))
    }
    setGuardando(false)
  }

  const mal = (k) => faltan.includes(k) || (k === 'fecha_compromiso' && faltan.includes('fecha_compromiso_pasada'))
  const inp = (k) => `input ${mal(k) ? '!border-rose-400' : ''}`
  const lbl = 'block text-xs font-medium text-gray-500 mb-1'

  return (
      <form onSubmit={crear} className="space-y-5">
        <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('ot.servicio.queEs')}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t('ot.campos.categoria')} *</label>
              <select className={inp('categoria')} value={f.categoria_id} onChange={e => set('categoria_id', e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.proyecto')} *</label>
              <SelectorProyecto className={inp('proyecto')} proyectos={proyectos} value={f.proyecto_id} onChange={v => set('proyecto_id', v)}
                placeholder={t('ot.nueva.buscarProyecto')} textoVacio={t('ot.nueva.elegir')} />
            </div>
          </div>
          <div>
            <label className={lbl}>{t('ot.servicio.queHacer')} *</label>
            <textarea rows={3} className={inp('descripcion')} value={f.descripcion} placeholder={t('ot.servicio.queHacerEj')}
              onChange={e => set('descripcion', e.target.value)} />
          </div>
          <div>
            <label className={lbl}>{t('ot.campos.ot_relacionada')} ({t('ot.admision.opcional')})</label>
            <input list="ots-relacionables" className={inp('ot_relacionada')} value={f.ot_relacionada}
              placeholder={t('ot.servicio.otRelacionadaEj')} onChange={e => set('ot_relacionada', e.target.value)} />
            <datalist id="ots-relacionables">
              {opcionesOt.map(o => <option key={o.id} value={o.txt} />)}
            </datalist>
            <p className="text-[11px] text-gray-400 mt-1">{t('ot.servicio.otRelacionadaAyuda')}</p>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('ot.admision.planificacion')}</h3>
          <div>
            <span className={lbl}>{t('ot.campos.prioridad')} *</span>
            <div className={`inline-flex rounded-lg border p-0.5 ${mal('prioridad') ? 'border-rose-400' : 'border-gray-200'}`}>
              {PRIORIDADES.map(p => (
                <button key={p} type="button" onClick={() => set('prioridad', p)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${f.prioridad === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {t(`ot.prioridades.${p}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t('ot.campos.fecha_compromiso')} *</label>
              <input type="date" min={hoyLocal()} className={inp('fecha_compromiso')} value={f.fecha_compromiso}
                onChange={e => set('fecha_compromiso', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.horas_estimadas')}</label>
              <input type="number" min="0" step="0.5" className="input" value={f.horas_estimadas}
                placeholder={t('ot.admision.opcional')} onChange={e => set('horas_estimadas', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.grupo')} *</label>
              <select className={inp('grupo')} value={f.grupo_id} onChange={e => elegirGrupo(e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.responsable')} *</label>
              <select className={inp('responsable')} value={f.responsable_id} disabled={!f.grupo_id} onChange={e => set('responsable_id', e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {miembrosDe(grupos, f.grupo_id).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              {!f.grupo_id && <p className="text-[11px] text-gray-400 mt-1">{t('ot.planificar.primeroGrupo')}</p>}
              {f.grupo_id && !miembrosDe(grupos, f.grupo_id).length && <p className="text-[11px] text-amber-700 mt-1">{t('ot.planificar.grupoSinPersonal')}</p>}
            </div>
          </div>
        </section>

        {faltan.length > 0 && (
          <p className="text-sm text-rose-700">
            {t('ot.servicio.revisa', { lista: faltan.map(x => t(`ot.faltan.${x}`, { defaultValue: x })).join(', ') })}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <p className="text-xs text-gray-500 text-right">{t('ot.servicio.notaCrear')}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => navigate('/operations/planta/ot')}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
            {t('common:actions.cancel')}
          </button>
          <button type="submit" disabled={guardando}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            {guardando ? t('common:actions.saving') : t('ot.servicio.crear')}
          </button>
        </div>
      </form>
  )
}
